import React from 'react';
import { DollarSign, Clock, TrendingUp, Package, Zap, Users, AlertCircle } from 'lucide-react';

export default function ROIDashboard() {
  // 10 Procurement Metrics
  const procurementMetrics = {
    // MONEY SAVED
    claimsRecovered: { value: 12450, unit: 'this month', icon: AlertCircle, color: 'green' },
    deadStockPrevented: { value: 5000, unit: 'this month', icon: Package, color: 'green' },
    priceOptimization: { value: 8340, unit: 'this month', icon: DollarSign, color: 'green' },
    expeditedShippingAvoided: { value: 3000, unit: 'this month', icon: TrendingUp, color: 'green' },
    volumeDiscounts: { value: 1500, unit: 'this month', icon: Zap, color: 'green' },

    // TIME SAVED (Labor Value)
    poChasingHours: { value: '56 hrs/week', subvalue: '2.3 FTE', unit: '$1,680/month', icon: Clock, color: 'blue' },
    invoiceReconciliation: { value: '18 hrs/month', unit: '$540/month saved', icon: Clock, color: 'blue' },
    exceptionResearch: { value: '2.4 hrs/week', unit: '$360/month saved', icon: Clock, color: 'blue' },

    // REVENUE & CASH
    fillRateRevenue: { value: '$270K/month', unit: 'from +3% fill rate', icon: TrendingUp, color: 'green' },
    cashFlowImprovement: { value: '$500K', unit: '+$2K/month early pay discounts', icon: DollarSign, color: 'blue' },
  };

  const MoneyCard = ({ icon: Icon, label, value, unit, detail }) => (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-white/60 text-xs mb-1">{label}</p>
          <p className="text-white font-bold text-lg">{value}</p>
          {detail && <p className="text-white/40 text-xs mt-1">{detail}</p>}
        </div>
        <Icon size={20} className="text-green-400 opacity-50 flex-shrink-0 ml-2" />
      </div>
      <p className="text-white/50 text-[11px]">{unit}</p>
    </div>
  );

  const TimeCard = ({ icon: Icon, label, value, subvalue, unit }) => (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-white/60 text-xs mb-1">{label}</p>
          <p className="text-white font-bold text-lg">{value}</p>
          {subvalue && <p className="text-[#2E6BFF] text-sm font-medium">{subvalue}</p>}
        </div>
        <Icon size={20} className="text-[#2E6BFF] opacity-50 flex-shrink-0 ml-2" />
      </div>
      <p className="text-white/50 text-[11px]">{unit}</p>
    </div>
  );

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Direct Savings Dashboard</h2>
        <p className="text-white/60">Bottom-line money saved from procurement optimization</p>
      </div>

      {/* HERO: Direct Savings */}
      <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-2 border-green-500/50 rounded-lg p-8">
        <div className="text-center mb-8">
          <p className="text-green-400/80 text-sm mb-2">DIRECT MONTHLY SAVINGS</p>
          <p className="text-6xl font-bold text-green-400">$30.2K</p>
          <p className="text-white/60 text-sm mt-3">Procurement optimization directly recovered to bottom line</p>
          <p className="text-white/40 text-xs mt-2">YTD: $87.4K | Payback: 4.2 months</p>
        </div>

        {/* 5 Direct Savings Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-8 pt-8 border-t border-green-500/20">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">$12.4K</p>
            <p className="text-white/60 text-xs mt-1">Claims Recovered</p>
            <p className="text-white/40 text-[10px] mt-1">47 short-ships claimed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">$8.3K</p>
            <p className="text-white/60 text-xs mt-1">Price Optimization</p>
            <p className="text-white/40 text-[10px] mt-1">Lowest-cost suppliers</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">$5K</p>
            <p className="text-white/60 text-xs mt-1">Dead Stock Prevented</p>
            <p className="text-white/40 text-[10px] mt-1">Better forecasting</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">$3K</p>
            <p className="text-white/60 text-xs mt-1">Expedited Avoided</p>
            <p className="text-white/40 text-[10px] mt-1">Rush orders prevented</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">$1.5K</p>
            <p className="text-white/60 text-xs mt-1">Volume Discounts</p>
            <p className="text-white/40 text-[10px] mt-1">Consolidated orders</p>
          </div>
        </div>
      </div>

      {/* How the $30.2K is Generated */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">How Direct Savings Work</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Claims Recovery</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$12.4K/month</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">47 short-ships detected and claimed automatically. No manual chasing required. Claims Agent files instantly.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <DollarSign size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Price Optimization</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$8.3K/month</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">Orders allocated to lowest-cost suppliers. Scoring Agent tracks price variance and reallocates automatically.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <Package size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Dead Stock Prevention</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$5K/month</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">Demand Agent forecasts from 342 POS data points. Prevents overstocking and spoilage on fresh goods.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <TrendingUp size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Expedited Shipping Avoided</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$3K/month</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">Better forecasting eliminates rush orders. Orders shipped via standard delivery instead of expedited.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <Zap size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Volume Discounts</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$1.5K/month</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">Orders consolidated to 2-3 reliable suppliers. Achieves bulk discount tiers automatically.</p>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <DollarSign size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Total Direct Monthly</p>
                <p className="text-2xl font-bold text-green-400 mt-1">$30.2K</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">These are direct, quantifiable savings that go straight to your bottom line. No qualitative estimates.</p>
          </div>
        </div>
      </div>

      {/* Bottom Line */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-white mb-4">The Bottom Line</h3>
        <p className="text-white/70 mb-6">These savings are direct, recurring, and provable:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-white/60 text-xs mb-2">MONTHLY SAVINGS</p>
            <p className="text-3xl font-bold text-green-400">$30.2K</p>
            <p className="text-white/40 text-xs mt-2">Goes straight to profit</p>
          </div>
          <div className="border-l border-r border-white/10 px-6">
            <p className="text-white/60 text-xs mb-2">ANNUAL SAVINGS</p>
            <p className="text-3xl font-bold text-green-400">$362K</p>
            <p className="text-white/40 text-xs mt-2">Recurring year after year</p>
          </div>
          <div>
            <p className="text-white/60 text-xs mb-2">PAYBACK PERIOD</p>
            <p className="text-3xl font-bold text-green-400">4.2 mo</p>
            <p className="text-white/40 text-xs mt-2">ROI positive immediately</p>
          </div>
        </div>
      </div>
    </div>
  );
}
